/**
 * BM Diagrams and Packages Types
 *
 * Branded types and interfaces for BM diagrams organization.
 */

import type { TBmClassId, TBmFieldId } from "./bm";

// ============================================================================
// Branded Types (Type-safe IDs)
// ============================================================================

declare const __brand: unique symbol;
type Brand<T, B> = T & { [__brand]: B };

/** BM Package ID (branded i64) */
export type TBmPackageId = Brand<number, "BmPackageId">;

/** BM Diagram ID (branded i64) */
export type TBmDiagramId = Brand<number, "BmDiagramId">;

/** BM Diagram Object ID (branded i64) */
export type TBmDiagramObjectId = Brand<number, "BmDiagramObjectId">;

/** BM Hidden Edge ID (branded i64) */
export type TBmHiddenEdgeId = Brand<number, "BmHiddenEdgeId">;

/** Type of edge in BM diagram */
export type TBmEdgeType = "field" | "inheritance";

// ============================================================================
// Entities
// ============================================================================

/** BM Package for organizing diagrams */
export interface IBmPackage {
  readonly id: TBmPackageId;
  readonly name: string;
  readonly parentId: TBmPackageId | null;
  readonly ordinal: number;
  readonly createdAt: string;
}

/** BM Diagram belonging to a package */
export interface IBmDiagram {
  readonly id: TBmDiagramId;
  readonly name: string;
  readonly packageId: TBmPackageId;
  readonly description: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Object (class) placed on a diagram with coordinates */
export interface IBmDiagramObject {
  readonly id: TBmDiagramObjectId;
  readonly diagramId: TBmDiagramId;
  readonly classId: TBmClassId;
  readonly x: number;
  readonly y: number;
  readonly width: number | null;
  readonly createdAt: string;
}

/** Hidden edge on a diagram */
export interface IBmHiddenEdge {
  readonly id: TBmHiddenEdgeId;
  readonly diagramId: TBmDiagramId;
  readonly edgeType: TBmEdgeType;
  readonly fieldId: TBmFieldId | null;
  readonly childClassId: TBmClassId | null;
  readonly createdAt: string;
}

/** Diagram with all its objects */
export interface IBmDiagramWithObjects extends IBmDiagram {
  readonly objects: readonly IBmDiagramObject[];
  readonly hiddenEdges: readonly IBmHiddenEdge[];
}

/** Package tree node with counts (for tree view) */
export interface IBmPackageTreeNode {
  readonly id: TBmPackageId;
  readonly name: string;
  readonly parentId: TBmPackageId | null;
  readonly ordinal: number;
  readonly diagramCount: number;
  readonly childCount: number;
}

// ============================================================================
// Request DTOs
// ============================================================================

/** Request to create a new package */
export interface ICreateBmPackageRequest {
  readonly featureId: number;
  readonly name: string;
  readonly parentId?: TBmPackageId;
  readonly ordinal?: number;
}

/** Request to update a package */
export interface IUpdateBmPackageRequest {
  readonly name?: string;
  /**
   * Подпакет можно вынести в корень: undefined — не менять, null — сделать
   * пакет корневым, идентификатор — перенести внутрь него
   * (spec/features/business-model/packages.md, R18).
   */
  readonly parentId?: TBmPackageId | null;
  readonly ordinal?: number;
}

/** Request to create a new diagram */
export interface ICreateBmDiagramRequest {
  readonly featureId: number;
  readonly name: string;
  readonly packageId: TBmPackageId;
  readonly description?: string;
}

/** Request to update a diagram */
export interface IUpdateBmDiagramRequest {
  readonly name?: string;
  readonly packageId?: TBmPackageId;
  /**
   * Описание можно очистить: undefined — не менять, null — стереть
   * (spec/features/business-model/diagrams.md, R04).
   */
  readonly description?: string | null;
}

/** Request to add an object to a diagram */
export interface IAddDiagramObjectRequest {
  readonly classId: TBmClassId;
  readonly x?: number;
  readonly y?: number;
  readonly width?: number | null;
}

/** Request to update object position */
export interface IUpdateObjectPositionRequest {
  readonly x: number;
  readonly y: number;
  readonly width?: number | null;
}

/** Request to hide an edge */
export interface IHideEdgeRequest {
  readonly edgeType: TBmEdgeType;
  readonly fieldId?: TBmFieldId | null;
  readonly childClassId?: TBmClassId | null;
}

/** Request to show an edge */
export interface IShowEdgeRequest {
  readonly edgeType: TBmEdgeType;
  readonly fieldId?: TBmFieldId | null;
  readonly childClassId?: TBmClassId | null;
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Create a BmPackageId from a number */
export function createBmPackageId(id: number): TBmPackageId {
  return id as TBmPackageId;
}

/** Create a BmDiagramId from a number */
export function createBmDiagramId(id: number): TBmDiagramId {
  return id as TBmDiagramId;
}

/** Create a BmDiagramObjectId from a number */
export function createBmDiagramObjectId(id: number): TBmDiagramObjectId {
  return id as TBmDiagramObjectId;
}

/** Check if a value is a valid BmPackageId */
export function isBmPackageId(value: unknown): value is TBmPackageId {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

/** Check if a value is a valid BmDiagramId */
export function isBmDiagramId(value: unknown): value is TBmDiagramId {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

// ============================================================================
// Tree Node Type (for ElTree)
// ============================================================================

/** Tree node type for packages/diagrams/classes tree */
export type TPackageTreeNodeType = "package" | "diagram" | "class";

/** Tree node data for ElTree */
export interface IPackageTreeNode {
  readonly id: string; // format: "pkg_123", "dgm_456", or "cls_789"
  readonly label: string;
  readonly type: TPackageTreeNodeType;
  readonly data:
    | IBmPackageTreeNode
    | IBmDiagram
    | import("./bm").IBmClass;
  readonly children?: IPackageTreeNode[];
  readonly isLeaf?: boolean;
}
